import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, EnvelopeSimple, MapPin, ArrowRight } from "@phosphor-icons/react";
import PageHero from "../components/PageHero";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
    RadioGroup, RadioGroupItem,
} from "../components/ui/radio-group";
import { BRAND, SERVICE_OPTIONS } from "../lib/data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        service: "",
        property_type: "Residential",
        address: "",
        project_details: "",
    });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.phone || !form.email || !form.service) {
            toast.error("Please fill in your name, phone, email, and service.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/quotes`, form);
            toast.success("Thanks! We'll reach out within one business day.");
            setSubmitted(true);
        } catch (err) {
            const msg = err?.response?.data?.detail || "Something went wrong. Please call us instead.";
            toast.error(typeof msg === "string" ? msg : "Submission failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <PageHero
                eyebrow="Get in touch"
                title="Request your free quote."
                sub="Tell us about your property and we'll be in touch within one business day. Calls answered Monday–Saturday."
            />

            <section className="px-4 sm:px-8 pb-24">
                <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
                    {/* Contact info */}
                    <aside className="lg:col-span-4 space-y-8">
                        <div className="rounded-2xl bg-brand-primary text-white p-8">
                            <h3 className="font-serif text-2xl mb-5">Speak with us</h3>
                            <ul className="space-y-5 text-[#D6D3C2]">
                                <li className="flex items-start gap-3">
                                    <Phone size={20} weight="duotone" className="mt-0.5 text-brand-secondary" />
                                    <div className="flex flex-col">
                                        <a href={`tel:${BRAND.officePhone.replace(/\D/g, "")}`} data-testid="contact-office-phone" className="hover:text-white">Office: {BRAND.officePhone}</a>
                                        <a href={`tel:${BRAND.cellPhone.replace(/\D/g, "")}`} data-testid="contact-cell-phone" className="hover:text-white">Cell: {BRAND.cellPhone}</a>
                                    </div>
                                </li>
                                <li className="flex items-start gap-3">
                                    <EnvelopeSimple size={20} weight="duotone" className="mt-0.5 text-brand-secondary" />
                                    <a href={`mailto:${BRAND.email}`} data-testid="contact-email" className="hover:text-white break-all">{BRAND.email}</a>
                                </li>
                                <li className="flex items-start gap-3">
                                    <MapPin size={20} weight="duotone" className="mt-0.5 text-brand-secondary" />
                                    <span>{BRAND.address}</span>
                                </li>
                            </ul>
                            <a href={`tel:${BRAND.officePhone.replace(/\D/g, "")}`} data-testid="contact-call-cta" className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-bg text-brand-primary text-sm font-semibold hover:bg-white transition-all">
                                Click to call <ArrowRight size={16} weight="bold" />
                            </a>
                        </div>
                        <div className="rounded-2xl overflow-hidden border border-brand-border h-72">
                            <iframe
                                title="Great Outdoors Landscaping Map"
                                src="https://www.google.com/maps?q=313%20Park%20Avenue%20G2%20Falls%20Church%20VA%2022046&output=embed"
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                data-testid="contact-map"
                            />
                        </div>
                    </aside>

                    {/* Form */}
                    <div className="lg:col-span-8">
                        <div className="rounded-2xl bg-brand-surface border border-brand-border p-6 sm:p-10">
                            {submitted ? (
                                <div data-testid="contact-success" className="py-12 text-center">
                                    <div className="font-serif text-4xl text-brand-primary font-semibold mb-3">Thanks — message received.</div>
                                    <p className="text-brand-textMuted max-w-md mx-auto">A member of the team will be in touch within one business day. For urgent requests, please call <a href={`tel:${BRAND.officePhone.replace(/\D/g, "")}`} className="underline">{BRAND.officePhone}</a>.</p>
                                    <button
                                        type="button"
                                        onClick={() => { setSubmitted(false); setForm({ name: "", phone: "", email: "", service: "", property_type: "Residential", address: "", project_details: "" }); }}
                                        data-testid="contact-reset"
                                        className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-primary text-white text-sm font-semibold hover:bg-brand-primaryHover transition-all"
                                    >
                                        Submit another request
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={onSubmit} data-testid="quote-form" className="space-y-6">
                                    <div>
                                        <h2 className="font-serif text-3xl text-brand-primary font-medium">Request your free quote</h2>
                                        <p className="text-sm text-brand-textMuted mt-1">All fields marked * are required.</p>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-5">
                                        <div className="space-y-2">
                                            <Label htmlFor="name">Full name *</Label>
                                            <Input id="name" data-testid="form-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Jane Doe" required />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="phone">Phone *</Label>
                                            <Input id="phone" data-testid="form-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(703) 555-0100" required />
                                        </div>
                                        <div className="space-y-2 sm:col-span-2">
                                            <Label htmlFor="email">Email *</Label>
                                            <Input id="email" type="email" data-testid="form-email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@example.com" required />
                                        </div>
                                        <div className="space-y-2 sm:col-span-2">
                                            <Label htmlFor="address">Project address</Label>
                                            <Input id="address" data-testid="form-address" value={form.address} onChange={(e) => update("address", e.target.value)} placeholder="Street, City, State" />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="service">Service needed *</Label>
                                            <Select value={form.service} onValueChange={(v) => update("service", v)}>
                                                <SelectTrigger id="service" data-testid="form-service-trigger">
                                                    <SelectValue placeholder="Choose a service" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {SERVICE_OPTIONS.map((opt) => (
                                                        <SelectItem key={opt} value={opt} data-testid={`form-service-${opt}`}>{opt}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Property type</Label>
                                            <RadioGroup
                                                value={form.property_type}
                                                onValueChange={(v) => update("property_type", v)}
                                                className="flex gap-5 pt-2"
                                            >
                                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                                    <RadioGroupItem value="Residential" id="pt-res" data-testid="form-pt-residential" />
                                                    Residential
                                                </label>
                                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                                    <RadioGroupItem value="Commercial" id="pt-com" data-testid="form-pt-commercial" />
                                                    Commercial
                                                </label>
                                            </RadioGroup>
                                        </div>
                                        <div className="space-y-2 sm:col-span-2">
                                            <Label htmlFor="project_details">Project details</Label>
                                            <Textarea
                                                id="project_details"
                                                data-testid="form-details"
                                                value={form.project_details}
                                                onChange={(e) => update("project_details", e.target.value)}
                                                rows={5}
                                                placeholder="Tell us about your property, timeline, and goals."
                                            />
                                        </div>
                                    </div>
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
                                        <p className="text-xs text-brand-textMuted">We'll never share your info. Replies typically within one business day.</p>
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            data-testid="form-submit"
                                            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-brand-primary text-white text-sm font-semibold hover:bg-brand-primaryHover transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                            {loading ? "Sending..." : "Request Your Free Quote"} {!loading && <ArrowRight size={16} weight="bold" />}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
